// PostHog analytics provider.
//
// One concrete implementation of AnalyticsProvider. The app never imports
// this directly — the facade (../index.ts) instantiates it when the runtime
// config reports `provider: "posthog"`. The facade owns the
// already-initialized guarding, so the methods here assume init() succeeded.
//
// Privacy posture (carried over from the original integration): this app
// shows sensitive legal/matter data (chat content, document text, entity
// names, emails). If autocapture / session replay are enabled below, replay
// is configured to mask aggressively — every input and all on-screen text
// is masked before it leaves the browser, so recordings show
// layout/interaction shape, not content. Revisit the mask settings
// deliberately if you ever need to see real text. Event *properties* are
// NOT masked — see the facade's note on keeping PII out of payloads.

import posthog from 'posthog-js';
import type { AnalyticsProvider, AnalyticsConfig } from '../types';
import type { CurrentUser } from '../../stores/authStore';

export class PostHogProvider implements AnalyticsProvider {
    async init(config: AnalyticsConfig): Promise<void> {
        posthog.init(config.key, {
            api_host: config.host,
            defaults: '2026-05-30',
            // Autocapture pageviews + element interactions.
            // autocapture: true,
            // capture_pageview: true,
            // capture_pageleave: true,

            // // We identify explicitly post-login (see identify); don't
            // // also create anonymous person profiles for signed-out traffic.
            // person_profiles: 'identified_only',

            // // Session replay, masked hard for sensitive legal data:
            // //   - maskAllInputs: every <input>/<textarea> value is masked.
            // //   - maskTextSelector '*': all text nodes are masked too, so
            // //     chat answers, entity names, and document text never leave
            // //     the browser in a recording.
            // disable_session_recording: false,
            // session_recording: {
            //     maskAllInputs: true,
            //     maskTextSelector: '*',
            // },
        });
    }

    // Send only the OIDC `sub` as the distinct id — no name/email — to keep
    // PII out of PostHog by default. Add properties here if a deployment
    // decides richer person profiles are acceptable.
    identify(user: CurrentUser): void {
        if (!user?.sub) return;
        posthog.identify(user.sub);
    }

    reset(): void {
        posthog.reset();
    }

    capture(event: string, properties?: Record<string, unknown>): void {
        posthog.capture(event, properties);
    }

    // PostHog's own pageview event name; the SPA router fires this on every
    // client-side navigation (autocapture's pageview only catches full loads).
    page(name: string, properties?: Record<string, unknown>): void {
        posthog.capture('$pageview', { $current_url: name, ...properties });
    }
}
